
import React from "react";
import { useKits } from "@/hooks/useKits";
import { useKitImages } from "@/hooks/useKitImages";
import { useMessages } from "@/hooks/useMessages";
import { useAlerts } from "@/hooks/useAlerts";
import { TickerPreview } from "@/components/TickerPreview";
import { TickerSettings } from "@/components/TickerSettings";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useKitMarket } from "@/hooks/useKitMarket";

export default function Settings() {
  const { kits, updateKitMutation } = useKits();
  const { toast } = useToast();
  
  const handleUpdateKit = (kitId: string, updates: Partial<typeof kits[0]>) => {
    const kit = kits.find(k => k.id === kitId);
    if (!kit) return;

    updateKitMutation.mutate({
      ...kit,
      ...updates
    });
  };
  
  return (
    <div className="min-h-screen bg-[#1A1F2C] text-white">
      <div className="container mx-auto py-8 space-y-8 relative">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Ticker Settings</h1>
          <Link to="/">
            <Button 
              variant="destructive" 
              size="icon"
              className="rounded-full hover:bg-red-600 transition-colors"
            >
              <X className="h-4 w-4" />
            </Button>
          </Link>
        </div>
        
        <div className="space-y-8">
          {kits.map((kit) => {
            const { kitImageConfigs } = useKitImages(kit.id);
            const { messages } = useMessages(kit.id);
            const { alerts } = useAlerts(kit.id);
            const { kitMarket } = useKitMarket(kit.id);
            
            const activeMessages = messages
              .filter(msg => msg.is_active)
              .map(msg => msg.text);

            const currentAlertMessage = alerts.find(alert => alert.is_active)?.text;
            
            return (
              <Card key={kit.id} className="p-6 space-y-4 bg-black/20 border-white/10">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-white">Kit {kit.number}: {kit.name}</h2>
                  <div className="flex gap-2">
                    <Link to={`/ticker/${kit.id}`}>
                      <Button variant="secondary" className="bg-white/10 hover:bg-white/20 text-white">
                        View Ticker
                      </Button>
                    </Link>
                    <Link to={`/sb/${kit.number}`}>
                      <Button variant="secondary" className="bg-white/10 hover:bg-white/20 text-white">
                        View Scoreboard
                      </Button>
                    </Link>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="h-24 overflow-hidden rounded-lg">
                    <TickerPreview 
                      messages={activeMessages}
                      speed={kit.speed}
                      backgroundColor={kit.background_color}
                      textColor={kit.text_color}
                      alertMessage={currentAlertMessage}
                      alertImages={kitImageConfigs.map(config => ({
                        storage_path: config.storage_path || '',
                        duration: config.duration,
                        is_active: config.is_active,
                      }))}
                      selectedMarket={kitMarket?.market_short_name || "SAT"}
                    />
                  </div>
                  
                  <TickerSettings 
                    speed={kit.speed}
                    backgroundColor={kit.background_color}
                    textColor={kit.text_color}
                    onSpeedChange={(speed) => {
                      console.log('Updating speed to:', speed);
                      handleUpdateKit(kit.id, { speed });
                    }}
                    onBackgroundColorChange={(color) => {
                      handleUpdateKit(kit.id, { background_color: color });
                    }}
                    onTextColorChange={(color) => {
                      handleUpdateKit(kit.id, { text_color: color });
                    }}
                  />
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
